import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Form, ListGroup } from 'react-bootstrap';

/**
 * Lightweight searchable single-select (no extra npm deps).
 * options: [{ value, label, keywords? }]
 */
function SearchableSelect({
  options = [],
  value,
  onChange,
  placeholder = 'Type to search…',
  emptyLabel = 'No matches',
  required = false,
  disabled = false,
  id
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const selected = useMemo(
    () => options.find((o) => String(o.value) === String(value)) || null,
    [options, value]
  );

  useEffect(() => {
    if (!open) {
      setQuery(selected ? selected.label : '');
    }
  }, [selected, open]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery(selected ? selected.label : '');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [selected]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options.slice(0, 50);
    return options
      .filter((o) => {
        const hay = `${o.label} ${o.subtitle || ''} ${o.keywords || ''}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 50);
  }, [options, query]);

  useEffect(() => {
    setHighlight(0);
  }, [query, open]);

  const pick = (opt) => {
    onChange(opt ? String(opt.value) : '');
    setQuery(opt ? opt.label : '');
    setOpen(false);
  };

  const clear = () => {
    onChange('');
    setQuery('');
    setOpen(true);
    inputRef.current?.focus();
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true);
      return;
    }
    if (e.key === 'Escape') {
      setOpen(false);
      setQuery(selected ? selected.label : '');
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, Math.max(filtered.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (open && filtered[highlight]) {
        pick(filtered[highlight]);
      }
    }
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <div className="d-flex gap-1">
        <Form.Control
          id={id}
          ref={inputRef}
          type="text"
          autoComplete="off"
          disabled={disabled}
          required={required && !value}
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            if (value) onChange('');
          }}
          onFocus={() => {
            setOpen(true);
            if (selected) setQuery('');
          }}
          onKeyDown={onKeyDown}
        />
        {value && !disabled && (
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={clear}
            title="Clear"
            aria-label="Clear selection"
          >
            ×
          </button>
        )}
      </div>
      {/* Keep native required semantics for form submit when empty */}
      {required && (
        <input
          tabIndex={-1}
          autoComplete="off"
          style={{ opacity: 0, height: 0, position: 'absolute', pointerEvents: 'none' }}
          value={value || ''}
          onChange={() => {}}
          required
        />
      )}
      {open && !disabled && (
        <ListGroup
          className="shadow-sm"
          style={{
            position: 'absolute',
            zIndex: 1055,
            width: '100%',
            maxHeight: 320,
            overflowY: 'auto',
            marginTop: 2
          }}
        >
          {filtered.length === 0 ? (
            <ListGroup.Item className="text-muted small">{emptyLabel}</ListGroup.Item>
          ) : (
            filtered.map((opt, idx) => (
              <ListGroup.Item
                key={String(opt.value)}
                action
                active={idx === highlight || String(opt.value) === String(value)}
                onMouseEnter={() => setHighlight(idx)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(opt);
                }}
              >
                <div>{opt.label}</div>
                {opt.subtitle && (
                  <div
                    className="small"
                    style={{
                      opacity: 0.85,
                      whiteSpace: 'normal',
                      lineHeight: 1.3,
                      marginTop: 2
                    }}
                  >
                    {opt.subtitle}
                  </div>
                )}
              </ListGroup.Item>
            ))
          )}
        </ListGroup>
      )}
    </div>
  );
}

export default SearchableSelect;
